(function() {
  // Prevent duplicate execution (Script may be injected twice on some pages)
  if (window.v0Audio) {
    return;
  }

  /**
   * Verticaly 2.0 Audio System (Smart Jukebox)
   * Handles global BGM playback with zero-load strategy, fading, volume memory,
   * and cross-page persistence.
   */

class VerticalyBGMPlayer {
    constructor() {
        this.bgm = null;
        this.isPlaying = false;
        this.isLoading = false;
        this.tracks = window.v0AudioTracks || [];
        this.currentTrackIndex = 0;
        this.userActivated = false;
        this.saveInterval = null;

        // Load volume from storage or default to 0.3
        this.volume = parseFloat(localStorage.getItem('v0_audio_volume') || '0.3');
        this.fadeInterval = null;
        
        // Cross-page persistence state key
        this.STORAGE_KEY = 'v0_audio_persistent_state';
    }

    init() {
        // モバイルデバイスでは音楽プレイヤーを無効化
        if (window.innerWidth <= 768 || 'ontouchstart' in window && window.innerWidth <= 1024) {
            console.log('[v0Audio] Disabled on mobile device');
            return;
        }

        // Render Playlist
        this.renderPlaylist();

        // Check for persistent state and auto-resume
        this.checkAndResumePlayback();

        // Lazy init audio on first user gesture
        ['pointerdown', 'touchstart', 'keydown'].forEach(evt => {
            window.addEventListener(evt, () => {
                if (this.userActivated) return;
                this.userActivated = true;
                this.ensureAudio();
                this.updateUI();
                
                // If there's a saved state and user has interacted, try to resume
                const savedState = this.getSavedState();
                if (savedState && savedState.wasPlaying && !this.isPlaying) {
                    this.resumeFromState(savedState);
                }
            }, { passive: true, once: false }); // Not once, need to catch first interaction
        });

        // Bind playlist toggle
        const playlistToggle = document.getElementById('v0PlaylistToggle');
        if (playlistToggle) {
            playlistToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                const playlist = document.getElementById('v0Playlist');
                if (playlist) playlist.classList.toggle('active');
            });
        }

        // Close playlist when clicking outside
        document.addEventListener('click', (e) => {
            const playlist = document.getElementById('v0Playlist');
            if (playlist && playlist.classList.contains('active') && !e.target.closest('.v0-audio-controls')) {
                playlist.classList.remove('active');
            }
        });

        // Save state before page unload
        window.addEventListener('beforeunload', () => {
            if (this.isPlaying) {
                this.saveState();
            }
        });
        
        // Also save on visibility change (tab switch, app switch)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden' && this.isPlaying) {
                this.saveState();
            }
        });
    }

    // ========== Cross-Page Persistence Methods ==========
    
    getSavedState() {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            if (!saved) return null;
            
            const state = JSON.parse(saved);
            // Check if state is fresh (within last 30 seconds)
            const now = Date.now();
            const age = now - (state.timestamp || 0);
            
            if (age > 30000) { // 30 seconds timeout
                localStorage.removeItem(this.STORAGE_KEY);
                return null;
            }
            
            return state;
        } catch (e) {
            console.error('[v0Audio] Failed to parse saved state', e);
            return null;
        }
    }
    
    saveState() {
        if (!this.bgm || !this.isPlaying) return;
        
        const state = {
            wasPlaying: true,
            trackIndex: this.currentTrackIndex,
            currentTime: this.bgm.currentTime || 0,
            volume: this.volume,
            timestamp: Date.now()
        };
        
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('[v0Audio] Failed to save state', e);
        }
    }
    
    clearSavedState() {
        localStorage.removeItem(this.STORAGE_KEY);
    }
    
    startPeriodicSave() {
        // Clear any existing interval
        if (this.saveInterval) {
            clearInterval(this.saveInterval);
        }
        
        // Save state every 1 second while playing
        this.saveInterval = setInterval(() => {
            if (this.isPlaying && this.bgm) {
                this.saveState();
            }
        }, 1000);
    }
    
    stopPeriodicSave() {
        if (this.saveInterval) {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }
    }
    
    checkAndResumePlayback() {
        const savedState = this.getSavedState();
        if (!savedState || !savedState.wasPlaying) {
            return;
        }
        
        
        // Store the state for later use
        this.pendingResumeState = savedState;
        
        // Update UI to show "ready to resume" state
        // The actual playback will start after user interaction (browser policy)
        this.currentTrackIndex = savedState.trackIndex;
        this.volume = savedState.volume || 0.3;
        
        // Show visual indicator that music will resume
        this.showResumeIndicator();
    }
    
    showResumeIndicator() {
        // Add a subtle visual cue that music was playing
        const buttons = document.querySelectorAll('#v0AudioToggle');
        buttons.forEach(btn => {
            btn.classList.add('has-pending-resume');
        });
        
        // Also for compact player
        const compactBtn = document.getElementById('v0CompactBgmBtn');
        if (compactBtn) {
            compactBtn.classList.add('has-pending-resume');
        }
    }
    
    resumeFromState(state) {
        if (!state) return;
        
        
        this.currentTrackIndex = state.trackIndex;
        this.volume = state.volume || 0.3;
        
        // Play and seek to saved position
        this.playAtPosition(state.trackIndex, state.currentTime);
    }
    
    playAtPosition(trackIndex, seekTime) {
        this.userActivated = true;
        if (!this.ensureAudio()) return;
        if (!this.tracks || this.tracks.length === 0) {
            return;
        }

        this.currentTrackIndex = trackIndex;
        this.bgm.src = this.tracks[this.currentTrackIndex].src;
        
        // Set up seek after metadata loads
        const seekOnLoad = () => {
            if (seekTime > 0 && this.bgm.duration) {
                // Ensure we don't seek past the end
                const safeSeekTime = Math.min(seekTime, this.bgm.duration - 1);
                this.bgm.currentTime = safeSeekTime;
            }
            this.bgm.removeEventListener('loadedmetadata', seekOnLoad);
        };
        
        this.bgm.addEventListener('loadedmetadata', seekOnLoad);
        this.bgm.load();

        this.isLoading = true;
        this.isPlaying = false;
        this.updateUI();

        this.bgm.volume = 0;
        this.bgm.play().then(() => {
            this.isLoading = false;
            this.isPlaying = true;
            this.updateUI();
            this.startPeriodicSave();
            
            // Clear the pending state
            this.pendingResumeState = null;
        }).catch(err => {
            this.isLoading = false;
            this.isPlaying = false;
            this.updateUI();
        });
    }

    // ========== Original Methods (Modified) ==========

    ensureAudio() {
        if (this.bgm) return true;

        this.bgm = new Audio();
        this.bgm.loop = false; // Changed: We handle next track manually for cross-page sync
        this.bgm.crossOrigin = 'anonymous';
        this.bgm.volume = this.volume;
        this.bgm.preload = 'none';

        this.bgm.addEventListener('waiting', () => {
            this.isLoading = true;
            this.updateUI();
        });

        this.bgm.addEventListener('playing', () => {
            this.isLoading = false;
            this.isPlaying = true;
            this.updateUI();
            this.fadeIn();
            this.startPeriodicSave();
        });

        this.bgm.addEventListener('pause', () => {
            this.isPlaying = false;
            this.isLoading = false;
            this.updateUI();
            this.stopPeriodicSave();
        });

        this.bgm.addEventListener('ended', () => {
            this.playNext();
        });

        this.bgm.addEventListener('error', () => {
            this.isLoading = false;
            this.isPlaying = false;
            this.updateUI();
            this.stopPeriodicSave();
        });

        return true;
    }

    play(trackIndex = 0) {
        this.userActivated = true;
        if (!this.ensureAudio()) return;
        if (!this.tracks || this.tracks.length === 0) {
            return;
        }

        // If switching tracks
        if (this.currentTrackIndex !== trackIndex || !this.bgm.src) {
            this.currentTrackIndex = trackIndex;
            this.bgm.src = this.tracks[this.currentTrackIndex].src;
            this.bgm.load();
        }

        this.isLoading = true;
        this.isPlaying = false;
        this.updateUI();

        this.bgm.volume = 0;
        this.bgm.play().then(() => {
            this.isLoading = false;
            this.isPlaying = true;
            this.updateUI();
            this.saveState(); // Save immediately on play
            this.startPeriodicSave();
        }).catch(err => {
            this.bgm.muted = true;
            this.bgm.play().then(() => {
                setTimeout(() => {
                    this.bgm.muted = false;
                    this.bgm.volume = this.volume;
                }, 50);
                this.isLoading = false;
                this.isPlaying = true;
                this.updateUI();
                this.saveState();
                this.startPeriodicSave();
            }).catch(e2 => {
                console.error('Audio play failed after fallback', e2);
                this.isLoading = false;
                this.isPlaying = false;
                this.updateUI();
            });
        });
    }

    pause() {
        if (!this.bgm) return;
        this.fadeOut(() => {
            this.bgm.pause();
            this.isPlaying = false;
            this.isLoading = false;
            this.clearSavedState(); // Clear state when user manually pauses
            this.stopPeriodicSave();
            this.updateUI();
        });
    }

    toggle() {
        // 確実にユーザーアクティベーションを設定
        this.userActivated = true;
        this.ensureAudio();
        
        if (this.isPlaying) {
            this.pause();
        } else {
            // Check if there's a pending resume state
            if (this.pendingResumeState) {
                this.resumeFromState(this.pendingResumeState);
            } else {
                this.play(this.currentTrackIndex);
            }
        }
    }

    playNext() {
        let nextIndex = this.currentTrackIndex + 1;
        if (nextIndex >= this.tracks.length) {
            nextIndex = 0;
        }
        this.play(nextIndex);
    }

    fadeIn() {
        if (this.fadeInterval) clearInterval(this.fadeInterval);
        const targetVolume = this.volume;
        let currentVol = 0;
        this.bgm.volume = 0;

        this.fadeInterval = setInterval(() => {
            currentVol += 0.05;
            if (currentVol >= targetVolume) {
                currentVol = targetVolume;
                clearInterval(this.fadeInterval);
            }
            this.bgm.volume = currentVol;
        }, 50);
    }

    fadeOut(callback) {
        if (this.fadeInterval) clearInterval(this.fadeInterval);
        let currentVol = this.bgm.volume;

        this.fadeInterval = setInterval(() => {
            currentVol -= 0.05;
            if (currentVol <= 0) {
                currentVol = 0;
                clearInterval(this.fadeInterval);
                if (callback) callback();
            }
            this.bgm.volume = currentVol;
        }, 50);
    }

    setVolume(vol) {
        this.volume = Math.max(0, Math.min(1, vol));
        if (this.bgm) {
            this.bgm.volume = this.volume;
        }
        localStorage.setItem('v0_audio_volume', this.volume);
    }

    renderPlaylist() {
        const container = document.getElementById('v0Playlist');
        if (!container) return;

        container.innerHTML = '';
        this.tracks.forEach((track, index) => {
            const item = document.createElement('div');
            item.className = `v0-track-item ${index === this.currentTrackIndex && this.isPlaying ? 'playing' : ''}`;
            item.textContent = track.title;
            item.onclick = () => {
                this.play(index);
                const playlist = document.getElementById('v0Playlist');
                if (playlist) playlist.classList.remove('active');
            };
            container.appendChild(item);
        });
    }

    updateUI() {
        const buttons = document.querySelectorAll('#v0AudioToggle');
        if (!buttons.length) {
        }

        buttons.forEach(btn => {
            btn.classList.toggle('is-loading', this.isLoading);
            btn.classList.toggle('is-playing', this.isPlaying && !this.isLoading);
            btn.classList.toggle('active', this.isPlaying || this.isLoading);
            btn.classList.remove('has-pending-resume');
            btn.setAttribute('aria-pressed', this.isPlaying ? 'true' : 'false');

            if (this.isLoading) {
                btn.innerHTML = `
                  <div style="display:flex;align-items:center;gap:6px;">
                    <svg class="v0-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                    </svg>
                    <span class="v0-control-text">LOAD</span>
                  </div>
                `;
            } else if (this.isPlaying) {
                btn.innerHTML = `
                  <div style="display:flex;align-items:center;gap:6px;">
                    <div class="v0-audio-wave"><span></span><span></span><span></span></div>
                    <span class="v0-control-text">ON</span>
                  </div>
                `;
            } else {
                // Check for pending resume state
                if (this.pendingResumeState) {
                    btn.innerHTML = '<span class="v0-control-text">▶ RESUME</span>';
                    btn.classList.add('has-pending-resume');
                } else {
                    btn.innerHTML = '<span class="v0-control-text">BGM</span>';
                }
            }
        });

        // Update playlist highlight
        this.renderPlaylist();
        
        // Also update compact player if exists
        this.syncCompactPlayer();
    }
    
    syncCompactPlayer() {
        const compactPlayer = document.getElementById('v0CompactAudio');
        const compactBtn = document.getElementById('v0CompactBgmBtn');
        const trackNameEl = document.getElementById('v0TrackName');
        
        if (compactPlayer) {
            compactPlayer.classList.toggle('is-playing', this.isPlaying);
        }
        
        if (compactBtn) {
            compactBtn.classList.remove('has-pending-resume');
            if (this.pendingResumeState && !this.isPlaying) {
                compactBtn.classList.add('has-pending-resume');
            }
        }
        
        if (trackNameEl && this.tracks[this.currentTrackIndex]) {
            trackNameEl.textContent = this.tracks[this.currentTrackIndex].title;
        }
    }
}

// Add styles for pending resume indicator
if (!document.getElementById('v0-audio-styles')) {
    const style = document.createElement('style');
    style.id = 'v0-audio-styles';
    style.textContent = `
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .v0-spinner { color: currentColor; }
        
        /* Pending resume indicator - pulsing glow */
        .has-pending-resume {
            animation: pendingPulse 2s ease-in-out infinite;
        }
        
        @keyframes pendingPulse {
            0%, 100% { 
                box-shadow: 0 0 5px rgba(0, 240, 255, 0.3);
            }
            50% { 
                box-shadow: 0 0 15px rgba(0, 240, 255, 0.6);
            }
        }
        
        .v0-control-btn.has-pending-resume {
            border-color: rgba(0, 240, 255, 0.5);
            color: #00F0FF;
        }
        
        .v0-compact-bgm-container.has-pending-resume {
            border-color: rgba(0, 240, 255, 0.5);
            background: rgba(0, 240, 255, 0.1);
        }
    `;
    document.head.appendChild(style);
}

// Initialize global audio instance
window.v0Audio = new VerticalyBGMPlayer();
window.VerticalyBGMPlayer = VerticalyBGMPlayer;
document.addEventListener('DOMContentLoaded', () => {
    window.v0Audio.init();

    // Bind to UI button
    const buttons = document.querySelectorAll('#v0AudioToggle');
    if (buttons.length) {
        buttons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                window.v0Audio.toggle();
            });
        });
        window.v0Audio.updateUI();
    }

    // Force initial UI update
    setTimeout(() => {
        if (window.v0Audio) {
            window.v0Audio.updateUI();
        }
    }, 100);

    // Initialize Mobile Scrub
    if (window.v0MobileScrub) {
        window.v0MobileScrub.init();
    }
});

/**
 * Verticaly 2.0 Mobile Scrub (Finger Hack)
 */
window.v0MobileScrub = {
    init: function () {
        const cards = document.querySelectorAll('.v0-card');
        cards.forEach(card => {
            const media = card.querySelector('.v0-card__media');
            const video = card.querySelector('.v0-card__video');

            if (!media || !video) return;

            let startX = 0;
            let isScrubbing = false;
            let videoDuration = 0;

            media.addEventListener('touchstart', (e) => {
                startX = e.touches[0].clientX;
                isScrubbing = true;
                videoDuration = video.duration || 30;

                if (video.preload === 'none') {
                    video.preload = 'metadata';
                    video.load();
                }
            }, { passive: true });

            media.addEventListener('touchmove', (e) => {
                if (!isScrubbing) return;

                const currentX = e.touches[0].clientX;
                const diff = currentX - startX;
                const width = media.offsetWidth;

                let seekPercent = (diff / width) * 100;

                if (video.readyState >= 1) {
                    const currentTime = video.currentTime;
                    const newTime = Math.max(0, Math.min(videoDuration, currentTime + (diff / width) * videoDuration * 0.1));
                    video.currentTime = newTime;
                    startX = currentX;
                }
            }, { passive: true });

            media.addEventListener('touchend', () => {
                isScrubbing = false;
            });
        });
    }
};

})(); // end guard
